import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, CircleMarker, Tooltip } from "react-leaflet";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Globe2, Search, MapPin, ArrowRight } from "lucide-react";

const TH_CENTER = [13.2, 100.9];

const ProvinceExplorer = () => {
  const [cities, setCities] = useState([]);
  const [q, setQ] = useState("");
  const [hover, setHover] = useState(null);
  const navigate = useNavigate();

  useEffect(() => { api.get("/cities").then((r) => setCities(r.data)).catch(() => {}); }, []);

  const filtered = useMemo(
    () => cities.filter((c) => c.name.toLowerCase().includes(q.toLowerCase())),
    [cities, q]
  );

  const go = (name) => navigate(`/search?from=${encodeURIComponent(name)}`);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-11 h-11 rounded-xl grid place-items-center glass-solid glow-cyan"><Globe2 className="text-[#22D3EE]" size={22} /></div>
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-semibold">Explore Thailand</h1>
          <p className="text-sm text-muted-cosmic">All 77 provinces — tap any star to find rides leaving from there.</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mt-4">
        <div className="lg:col-span-1">
          <div className="relative mb-3">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#22D3EE]" />
            <Input data-testid="explore-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a province…" className="pl-9 bg-white/5 border-white/10 text-white h-11" />
          </div>
          <Card className="glass p-2 max-h-[62vh] overflow-y-auto">
            {filtered.map((c) => (
              <button key={c.name} data-testid="explore-province-item" onClick={() => go(c.name)} onMouseEnter={() => setHover(c.name)} onMouseLeave={() => setHover(null)}
                className="w-full text-left px-3 py-2 rounded-lg hover:bg-white/8 flex items-center gap-2 group">
                <MapPin size={14} className="text-[#F7C948] shrink-0" />
                <span className="text-white text-sm flex-1">{c.name}</span>
                <ArrowRight size={14} className="text-[#22D3EE] opacity-0 group-hover:opacity-100 transition-opacity" />
              </button>
            ))}
            {filtered.length === 0 && <div className="p-4 text-center text-muted-cosmic text-sm">No province found</div>}
          </Card>
        </div>

        <div className="lg:col-span-2">
          <div className="h-[66vh] rounded-[var(--radius)] overflow-hidden border border-white/10">
            <MapContainer center={TH_CENTER} zoom={6} style={{ height: "100%", width: "100%" }} scrollWheelZoom={true}>
              <TileLayer attribution='&copy; OpenStreetMap contributors &copy; CARTO' url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png" />
              {cities.map((c) => (
                <CircleMarker key={c.name} center={c.coords}
                  radius={hover === c.name ? 9 : 5}
                  pathOptions={{ color: hover === c.name ? "#22D3EE" : "#F7C948", fillColor: hover === c.name ? "#22D3EE" : "#F7C948", fillOpacity: 0.9 }}
                  eventHandlers={{ click: () => go(c.name), mouseover: () => setHover(c.name), mouseout: () => setHover(null) }}>
                  <Tooltip>{c.name}</Tooltip>
                </CircleMarker>
              ))}
            </MapContainer>
          </div>
          <p className="text-xs text-muted-cosmic mt-2 text-center">Showing {cities.length} provinces · click a marker to search rides from that province</p>
        </div>
      </div>
    </div>
  );
};

export default ProvinceExplorer;
